import _ from 'lodash';
import { notificationCopy, notificationSettings, klaviyoFlowUrls } from '@shopmy/universal';
import { pluralizeCount } from './formatting';
import { humanizeCategory } from './notification_helpers';

export const STATUS_LABELS = { planned: 'Planned', live: 'Live', paused: 'Paused', retired: 'Retired' };

export const SHADOW_PERIOD_NONE =
  'Lands in Shadow and sends nothing. It can move to Allowlist or Live as soon as the gate passes.';
export const HOLDOUT_NONE = 'Once Live, every creator it fires for gets the push and the email.';
export const AUTO_LIVE_NOTE =
  'Setting Auto-Live as \u2018Yes\u2019 moves the lane to Live at the next Monday sweep, or at the next copy or Auto-Live action on the lane. Not recommended.';
export const AUTO_LIVE_REQUIRES_NO =
  'This can only be set to yes if Shadow and Holdout Group are both set to \u2018No\u2019.';

// The launch ladder, bottom up: shadow files receipts and sends nothing, allowlist sends to the listed creators, live sends.
export const ROLLOUT_MODES = ['shadow', 'allowlist', 'live'];

export const STAGE_LABELS = {
  planned: 'Planned',
  shadow: 'Shadow',
  allowlist: 'Allowlist',
  live: 'Live',
  paused: 'Paused',
  retired: 'Retired'
};

export const SENT_FROM_LABELS = { backend: 'Our backend', klaviyo_flow: 'Klaviyo flow', onesignal_dashboard: 'OneSignal dashboard' };

export const DELIVERED_BY_KLAVIYO = 'backend_klaviyo';
export const DELIVERED_BY_KLAVIYO_LABEL = 'Our backend, delivered by Klaviyo';
export const DELIVERED_BY_KLAVIYO_DELIVERY = 'Center row from our code. Push and email from the Klaviyo flow.';
export const DELIVERED_BY_KLAVIYO_EVENT_HINT = 'From the template. Lifecycle builds the flow on this event.';
export const DELIVERED_BY_KLAVIYO_PLANNED_EVENT_HINT = 'Engineering fills this in.';
export const KLAVIYO_FLOW_LINK_PLANNED_HINT = 'Optional for now. The Gate requires it before launch.';

// The Sent from chooser, in the order it offers them. Delivered by Klaviyo is stored as sentFrom
// backend, a Klaviyo push, and an email the flow designs.
export const SENT_FROM_CHOICES = [
  { value: 'backend', label: SENT_FROM_LABELS.backend, description: 'Our code decides and sends the push, email and Center row.' },
  {
    value: DELIVERED_BY_KLAVIYO,
    label: DELIVERED_BY_KLAVIYO_LABEL,
    description: 'Our code decides and writes the Center row. A Klaviyo flow sends the push and email.'
  },
  {
    value: 'klaviyo_flow',
    label: SENT_FROM_LABELS.klaviyo_flow,
    description: 'Klaviyo decides, from an event or a list or segment. Copy lives in Klaviyo.'
  },
  { value: 'onesignal_dashboard', label: SENT_FROM_LABELS.onesignal_dashboard, description: 'Sent by hand from the OneSignal console.' }
];
export const SENT_FROM_UNDECIDED = {
  value: '',
  label: 'Decide later',
  description: 'If unsure, leave blank for now. The Gate will ask before launch'
};
export const SENT_FROM_LOCKED_TIP = 'Set by the template in code. Change the template to change this.';

export const sentFromChoiceOf = ({ sentFrom, pushDelivery = null }) =>
  backendDeliveredByKlaviyo({ sentFrom, pushDelivery }) ? DELIVERED_BY_KLAVIYO : sentFrom || '';
export const sentFromFieldsFor = choice => ({
  sentFrom: choice === DELIVERED_BY_KLAVIYO ? 'backend' : choice || null,
  declaredPushDelivery: choice === DELIVERED_BY_KLAVIYO ? 'klaviyo' : null,
  declaredEmailDelivery: choice === DELIVERED_BY_KLAVIYO ? 'klaviyo_twin' : null
});
export const DELIVERED_BY_KLAVIYO_COPY_NOTE =
  'A Klaviyo flow sends this push and email. The words approved here are Lifecycle\u2019s starting point: they copy them into the flow, mark them Live in flow, and can edit them there. The Center row\u2019s words go live from here.';
export const DELIVERED_BY_KLAVIYO_EMAIL_BODY = 'The email body is designed in the flow.';
export const KLAVIYO_FLOW_URL_INVALID = 'The link recorded is not a Klaviyo flow (https://www.klaviyo.com/flow/\u2026).';

export const AUDIENCE_LABELS = { creator: 'Creator', shopper: 'Shopper', brand: 'Brand', manager: 'Manager' };

export const CHANNEL_LABELS = { push: 'Push', email: 'Email', center: 'Notification Center' };
const CHANNEL_ORDER = ['push', 'email', 'center'];

export const CENTER_BEHAVIOR_LABELS = {
  own_row: 'Own row',
  aggregated: 'Aggregated row',
  none: 'No row'
};

export const CENTER_IMAGE_LABELS = { link: 'Product image', brand: 'Brand logo', general: 'ShopMy logo' };

export const COPY_STATUS_LABELS = {
  needs_copy: 'Needs copy',
  in_draft: 'In draft',
  approved: 'Approved',
  in_code: 'From code',
  live_in_flow: 'Live in flow',
  covered_by_group: 'Covered by the group',
  revoked_on_owner: 'Revoked on owner',
  not_applicable: '—'
};

// The standard verbs for the Notification Center button; View is the default when none is chosen.
export const CENTER_CTA_VERBS = ['View', 'Respond', 'Accept', 'Approve', 'Complete', 'Share', 'Connect', 'Reconnect', 'Update'];

export const SURFACE_LABELS = {
  push_a: 'Push A',
  push_b: 'Push B',
  center_row: 'Notification Center row',
  center_row_several: 'Notification Center row · several',
  center_cta: 'Notification Center button',
  email_subject: 'Email subject',
  email_preview: 'Email preview',
  email_body: 'Email body'
};

// The copy rulebook the API validates with: surfaces, fields, limits, and the surfaces a notification may leave empty.
export const {
  SURFACE_FIELDS,
  isParagraphField,
  OPTIONAL_SURFACES,
  DRAFTABLE_SURFACES,
  applicableSurfaces,
  hasCopyWords,
  canDraftFromPlan,
  interpolateWithExamples,
  shouldRefuseUnknownCopyInputs,
  literalExampleVariable,
  validateCopyContent,
  approvalReplacesLiveSend,
  sendsFromBackend,
  KLAVIYO_EMAIL_COPY_NOTE,
  KLAVIYO_PUSH_COPY_NOTE,
  klaviyoCopyNote,
  isKlaviyoOwnedCopySurface,
  backendDeliveredByKlaviyo,
  usesKlaviyoFlow,
  isKlaviyoDeliveredSurface,
  launchStageFor,
  CASE_OPERATORS,
  CASE_OPERATOR_LABELS,
  CASE_FIELDS,
  MAX_CASES,
  supportsCases,
  casesOf,
  validateCaseSet,
  conflictingCasePairs,
  THREAD_CASE_FIELD,
  isEntityThread,
  lockedCases,
  statusCases
} = notificationCopy;

export const { isKlaviyoFlowUrl } = klaviyoFlowUrls;

// What a case's condition field is called in the editor. Anything a template counts for itself is
// shown as written, since the template names it.
export const CASE_FIELD_LABELS = {
  aggregateCount: 'how many arrived',
  aggregateValue: 'their total value',
  status: 'the status it moved to'
};

export const caseFieldLabel = name => CASE_FIELD_LABELS[name] || name;

// A thread's statuses as a person writes them: separated however they like, stored as the template
// spells them.
export const parseStatuses = value =>
  _.uniq(
    String(value || '')
      .split(/[,\n]/)
      .map(status => status.trim())
      .filter(Boolean)
  );

export const statusLabelOf = status => _.upperFirst(String(status || '').replace(/_/g, ' '));

// The one field a sentence branches on, as a person writes it: the field, a colon, then its values
// separated by commas. Text that does not make a `{ path, values }` the API accepts parses to null.
export const formatCaseEnum = declared => (declared?.path && Array.isArray(declared.values) ? `${declared.path}: ${declared.values.join(', ')}` : '');

export const parseCaseEnumText = text => {
  const raw = String(text || '').trim();
  if (!raw) return null;
  const splitAt = raw.indexOf(':');
  if (splitAt < 0) return null;
  return notificationCopy.caseEnumFrom({ path: raw.slice(0, splitAt), values: raw.slice(splitAt + 1).split(',') });
};

export const FIRST_STEP = 'first';

// A case's step is keyed apart from the first step, so a declared value named `first` cannot collide with it.
export const caseStepKey = value => `case:${value}`;

// How an aggregated row reads as the day grows: a single message while the day has one, then a day
// summary, split by case when the row branches on a declared word. Nothing for a Live Thread, whose
// statuses are their own path. The two names stay the same for every notification; the unit and
// the case words come from the template, so a planned row with none reads in plain fallbacks.
export const rowPathFor = rulebook => {
  if (rulebook?.centerBehavior !== 'aggregated' || isEntityThread(rulebook)) return null;
  const enumPath = rulebook.caseEnum?.path || null;
  const meanings = rulebook.caseMeanings || {};
  const unit = rulebook.centerUnit || 'event';
  const plural = `${unit}s`;
  const first = {
    key: FIRST_STEP,
    when: `After the first ${unit}`,
    uses: 'Single message',
    note: `A second ${unit} replaces this with the day summary.`
  };
  const later = enumPath
    ? rulebook.caseEnum.values.map(value => {
        const meaning = meanings[value];
        const condition = meaning?.condition || `${enumPath} is ${value}`;
        const kind = meaning?.kind || condition;
        return {
          key: caseStepKey(value),
          value,
          when: `More ${plural}, ${kind}`,
          uses: `Day summary · ${kind}`,
          condition,
          note: meaning?.note || `From the second ${unit} on, the day summary uses this when ${condition}.`
        };
      })
    : [
        {
          key: 'several',
          when: `More ${plural}`,
          uses: 'Day summary',
          note: `From the second ${unit} on, the row shows the day summary.`
        }
      ];
  return {
    steps: [first, ...later],
    conditions: _.fromPairs(later.filter(step => step.condition).map(step => [step.value, step.condition])),
    labels: { center_row: 'Single message', center_row_several: 'Day summary' },
    why: {
      center_row: `Shown until a second ${unit} arrives. The same row is then rewritten as the day summary.`,
      center_row_several: enumPath
        ? `Replaces the single message from the second ${unit} on. Write one version for each kind of day.`
        : `Replaces the single message from the second ${unit} on.`
    },
    caseNote: 'The small label is what the system calls each kind of day.'
  };
};

const { COPY_VARIABLE_PATHS, ALWAYS_KNOWN_INPUTS, AGGREGATE_VARIABLE_EXAMPLES, AGGREGATE_VARIABLE_SURFACES } = notificationCopy;

export const { rendererForChannels, unusedSettingGroupIdForTitle } = notificationSettings;
export const shortCodeFrom = notificationSettings.settingGroupIdForTitle;

// The recipient's name is the one variable every notification can render, so the rail offers it even
// when Inputs is empty.
const RECIPIENT_PILL = `${ALWAYS_KNOWN_INPUTS[0]}.firstName`;

// What a variable the rail offers on its own means, for the hover on its pill; the example on the
// pill is a sample value, not the meaning. A template's own variables carry their meaning on the
// input, so a pill with neither has no hover.
const VARIABLE_MEANINGS = {
  'user.firstName': 'The first name of the person receiving this.',
  'user.name': 'The full name of the person receiving this.',
  'user.lastName': 'The last name of the person receiving this.',
  'user.username': 'The ShopMy username of the person receiving this.',
  'brand.name': 'The name of the brand this is about.',
  aggregateCount: 'How many have landed in this row today, counting this one.',
  aggregateValue: 'The running total of their values today.',
  participantNames: 'The names of who or what is in the row today, joined with "and".'
};

// Every variable this notification's copy may actually render, in the order the rail shows them:
// each input, objects expanded to their allowlisted paths, then the recipient, and on the several
// sentence the day's running totals. A bare object is left out because the API refuses it, and a
// secret input never reaches copy at all.
export const copyVariablePills = (inputs = [], surface = null) => {
  const pills = inputs
    .filter(input => !input.secret)
    .flatMap(input => {
      const paths = COPY_VARIABLE_PATHS[input.name] || [];
      if (input.isObject && !paths.length) return [];
      if (!paths.length) return [{ name: input.name, example: input.example, meaning: input.meaning || null }];
      // An object's example describes it by the field the allowlist exposes, so it only fits a lone path.
      return paths.map(path => ({ name: `${input.name}.${path}`, example: paths.length === 1 ? input.example : null }));
    });
  const aggregates = AGGREGATE_VARIABLE_SURFACES.includes(surface)
    ? Object.entries(AGGREGATE_VARIABLE_EXAMPLES).map(([name, example]) => ({ name, example }))
    : [];
  return _.uniqBy([...pills, { name: RECIPIENT_PILL, example: null }, ...aggregates], 'name').map(pill => ({
    ...pill,
    meaning: pill.meaning || VARIABLE_MEANINGS[pill.name] || null
  }));
};

export const SURFACE_NOTES = {
  push_b: 'optional A/B variant',
  center_row_several: 'when more than one arrives in a day'
};

// What follows the limit under each copy field: where the words show. The number comes from the rulebook.
const FIELD_GUIDE_TAILS = {
  'push_a.title': 'one line on the lock screen.',
  'push_a.body': 'two lines on the lock screen.',
  'push_b.title': 'Empty means only Push A sends; approve both and sends split 50/50.',
  'center_row.text': 'up to three lines on a phone.',
  'center_row_several.text': 'The same sentence for a day with more than one, using count and names.',
  'email_preview.text': 'the line under the subject in the inbox.',
  'email_body.text': 'Paragraphs separated by a blank line. *italic* and **bold** are the only formatting.',
  'email_body.button': 'A verb phrase naming what opens, like View the order. Empty means no button; the email links in its sentences instead.',
  'email_body.buttonUrl': 'Choose an input such as {{ offer_url }}, or enter an HTTPS URL. This is where the button opens.'
};

// "Up to 40 characters, one line on the lock screen." A tail that starts with a capital is its own sentence.
export const fieldGuideFor = (surface, field) => {
  const limit = SURFACE_FIELDS[surface]?.[field];
  const lead = limit ? `Up to ${limit} characters` : 'No limit';
  const tail = FIELD_GUIDE_TAILS[`${surface}.${field}`];
  if (!tail) return `${lead}.`;
  return /^[A-Z]/.test(tail) ? `${lead}. ${tail}` : `${lead}, ${tail}`;
};

// How the API credits copy the model wrote; History and the surface line show it.

export const COPY_FIELD_LABELS = {
  title: 'Title',
  body: 'Body',
  text: 'Sentence',
  verb: 'Button verb',
  button: 'Button',
  buttonUrl: 'Button link'
};

export const copyFieldLabel = (surface, field) => (isParagraphField(surface, field) ? 'Paragraphs' : COPY_FIELD_LABELS[field]);

// Every (i) on the tab. One or two sentences, written for someone who has never read the docs.
export const TIPS = {
  name: 'The plain-English name shown in the Registry, the admin and the sweep. Example: Payout sent.',
  shortCode:
    'The notification\u2019s identifier in code: lowercase words joined by underscores, unique. It becomes the template file name, the type on every receipt, and the key everything else matches on. Set once at creation; changing it later is a rename in code.',
  sentFrom: [
    { text: 'Who decides each send, and who delivers it.' },
    {
      lead: 'Our backend',
      text: 'our code decides who gets it and when, then sends the push, the email and the Center row itself. Its copy lives here.'
    },
    {
      lead: DELIVERED_BY_KLAVIYO_LABEL,
      text:
        'our code decides and writes the Center row, then sends Klaviyo an event. A Klaviyo flow on that event delivers the push and email. It can\u2019t send a push until that flow is live.'
    },
    {
      lead: 'Klaviyo flow',
      text:
        'Klaviyo decides, either from an event our code fires or from a list or segment. Its copy lives in Klaviyo, and it skips Shadow, Allowlist and Live.'
    },
    { lead: 'OneSignal dashboard', text: 'sent by hand from the OneSignal console.' },
    { text: 'Not sure? Leave it blank. The Gate asks before anything leaves Shadow.', muted: true }
  ],
  audience: 'Who receives it. A notification can go to more than one kind of account.',
  budgetCategory:
    'Which frequency caps apply and which group of notifications this one is counted with. Response needed and Operational are uncapped because a brand or her money is waiting; the other categories share rolling 24-hour and 7-day caps per creator.',
  muteSwitch:
    'The setting a creator uses to turn this off, exactly as it reads on her Settings page. Pick an existing one so related notifications share a switch. Create a new one only when nothing fits; it appears in Settings on web and app when the template PR ships. No switch is for operational messages she must receive, and needs a reason.',
  noSwitchReason:
    'Why a creator cannot turn this one off, in one sentence: what she would miss if she could. Example: A security code; she asked for it and it expires. The Gate holds the notification in shadow until this is written.',
  switchGroup:
    'The section of her Settings page this switch sits under, like Earnings & Payments. Pick the group where she would look for it; start a new group only when nothing fits.',
  switchLabel:
    'The name of the switch as it reads on her Settings page. Short and about the topic, not the event. Example: Payouts, not Payout sent.',
  switchDescription:
    'The smaller line under the label that says what turning the switch off silences, in one plain sentence. Example: Get notified when a payout is on its way to you.',
  switchChannels: 'What the switch turns off: the push, the email, or both. Most switches cover both, so one setting silences the topic everywhere.',
  switchRecommended:
    'A recommended switch warns a creator before she turns it off, with a note about what she would miss. Use it when turning this off could cost her money or a deadline.',
  switchColumn:
    'The name the code stores this switch under. It fills in from the label; change it only if engineering asks for a specific name. Example: notifyOnPayouts.',
  switchDefault: 'Whether the switch starts on or off for everyone. On for everyone is normal; off means she only gets these after she opts in.',
  centerBehavior:
    'How this notification appears in the creator\u2019s in-app Notification Center. Own row: one row per send. Aggregated row: one row per day that grows as more arrive. Live Thread: one row per thing, rewritten as that thing moves.',
  threadOn:
    'Which order this notification stays on. Shipped, out for delivery, and delivered then update that same notification. For a lookbook shipment, type order.id. Type the same thing in Row anchor.',
  threadStatuses:
    'The steps this row moves through, in order, separated by commas. The row only updates for a status on this list. Example: shipped, out_for_delivery, delivered. The template\u2019s own list replaces this when it lands.',
  caseEnum:
    'One field this sentence can branch on, then its values, separated by commas. Example: mix: creators, shoppers, both. Each value gets its own sentence, compared only for equality, with nothing left over for the other cases. The template\u2019s own list replaces this when it lands.',
  centerImage:
    'The image that leads the row. Product image: the image of the product the row is about. Brand logo: the brand\u2019s logo, or its square badge and then its cover photo when it has none. ShopMy logo: the black square with the S, for anything about her money, records, tier or account.',
  owner:
    'The person accountable for this notification\u2019s plan and copy status. Defaults to the owner most notifications for the audience already have.',
  channels: 'Where it reaches her: a push, an email, a row in her Notification Center. Pick every one that applies.',
  productArea:
    'The part of the product this notification belongs to, from the audience\u2019s own list. Choose Other to type an area that is not there; it is saved as written and offered again next time.',
  purpose:
    'One sentence on why this notification exists and what the creator should know or do. Example: Tell the creator the money moved, with how much and where, before the processor\u2019s own email arrives.',
  trigger:
    'The event or job that fires it, where it lives in code, how often, and what stops a repeat. Example: One push per Payments insert in the payout run (Fridays 3pm ET, monthly 1st); dedupe on Payment id [payout_run.js].',
  inputs:
    'The values the trigger hands the notification, as name: example, one per line or separated by slashes, commas or semicolons. Example: amount: 907 / month: December declares two inputs. Copy may use only these as variables and the preview renders with them. Engineering confirms them; the template\u2019s declared inputs replace the list when it lands and every draft is re-checked.',
  tapLands:
    'Where a tap opens. One thing opens that thing; a row that rolls several up opens the section. Example: the collaboration, or Partners → Collaborations. Required to leave shadow when this notification has a Notification Center row.',
  rowAnchor:
    'Which events belong in the same notification. brand.id means one notification per brand that day. user.id means one notification for her whole day. When the notification updates as an order moves, use the same answer as Thread on. For a lookbook shipment, that is order.id.',
  emergencyOff:
    'How to stop future sends. Backend: set Mode to Shadow on this page. That marks it Paused and stops the push and the email. Klaviyo: set the flow to Manual in Klaviyo. The Registry does not stop that send. OneSignal: pause the Journey in OneSignal.',
  klaviyoUrl:
    'Paste the full Klaviyo flow URL, such as https://www.klaviyo.com/flow/ABC123/edit. Add link or the pencil opens it to edit. Required to leave shadow when Sent from is Klaviyo flow.',
  notificationCenterRationale:
    'Why this notification should or should not create a persistent Notification Center row. Example: Own row \u2014 she needs to find the payout after the push disappears. No row \u2014 the email is the record.',
  notes:
    'Anything the plan has no row for: fallbacks, variants, flags, edge cases. Example: Stripe payouts read "Stripe" as the method. Amounts under $1 are not sent.',
  rulebook:
    'What code enforces for this notification, read from its template. Nothing here can be edited in the admin; changing it is a pull request. Not in code yet means no template exists, so these are the values chosen when it was created.',
  stage:
    'Where the notification is. Paused and Retired come from its status. A Klaviyo flow, a OneSignal dashboard send, or a notification with no template also reads its status, because nothing on the ladder decides its sends. Everything else reads its launch mode, which is Shadow until a mode is set.',
  mode:
    'Where a backend notification sits. Shadow: records what it would send and sends nothing. Allowlist: sends to the people on this notification\u2019s list, and shadows everyone else. Live: sends to everyone except the holdout. The moves unlock when the Gate passes. If no one has chosen otherwise, it stays in Shadow and nothing is sent until someone moves it to Allowlist or Live. Klaviyo or OneSignal: the send is controlled there, not here. No template yet: the code has not landed. Paused: it was sending, and moving it back to Shadow stopped it.',
  shadowPeriod:
    'How long it must sit in Shadow, with at least one send to read, before Allowlist and Live unlock. The default is none. Changing it needs the launch notifications scope, and every change is in History.',
  holdout:
    "Once a lane is Live, the same share of creators are held out of the push and the email on every send, for as long as the holdout stays on. It is the same people on every notification that has a holdout, picked from their account id, so someone held out of one is held out of all of them. They start receiving it on the next send only when someone sets that lane's holdout to No. The Notification Center row is different. A held-out creator still gets the row. The holdout covers the push and the email.",
  plan:
    'What the team decided: why it exists, what fires it, where a tap lands, how to stop it. Anyone with marketing access can edit a row; every edit is in History.',
  copy:
    'The words for each place this notification appears. Type straight into a field; it saves when you leave it. Approve turns the draft into a numbered version. Backend push, email subject, and Notification Center go out on the next send. Email body and email preview are signed off here and ship in the template or the Klaviyo flow.',
  push: `Title up to ${SURFACE_FIELDS.push_a.title} characters, body up to ${SURFACE_FIELDS.push_a.body}. Variables are the names from Inputs. Push B is an optional A/B variant: once both are approved, sends split 50/50 by creator.`,
  centerRow:
    'One sentence about what happened to her, with the number or deadline in it, up to three lines on a phone. Example: $907 is on its way to you via PayPal.',
  centerRowSeveral:
    'The same sentence for a day with more than one event, using count and names. Example: Maya and Jo joined ShopMy through your link.',
  centerCta: `The word on the Needs Action button. Primary draws it filled black; Secondary draws it white. Pick one of the standard verbs, or write a custom verb of up to ${SURFACE_FIELDS.center_cta.verb} characters when none fits. Approved like any other copy.`,
  emailBody:
    'The email\u2019s paragraphs, separated by a blank line, with *italic* and **bold** as the only formatting. Open with the greeting, like Hi {{ user.firstName }}; the layout adds the standard ShopMy header and the sign-off, so write neither. The button is a verb phrase naming what opens, like View the order; leave it empty when the email links in its sentences instead. Until a template exists the preview shows the body in that layout; once one does, the template\u2019s own email replaces it and the body is read only here.',
  needsAction:
    'Pins the row in Needs Action until she handles it. The template decides this once it exists; until then, choose it here. Yes shows a button. No shows a row in the list.',
  ctaStyle:
    'Primary is the filled black button, for something she has to do, like reconnecting an account. Secondary is the white button, for something she can open, like viewing a collab. It does not decide which section the row is in.',
  showChevron:
    'Yes adds a chevron on the row so it reads as tappable. A deep link alone does not add one, because every row should open somewhere. It does not pin the row in Needs Action.',
  gate:
    'The things that must be true before this notification can leave shadow. Each row says what is true now and who fixes it. The list\u2019s Gate column is the short form.',
  copyStatus:
    'The state of the words. Needs copy: nothing yet. From code: nobody has written here, so the fields hold the template\u2019s own words rendered with the Inputs\u2019 examples; a name or brand in them is an example, not fixed copy. In draft: someone is writing it. Approved: signed off. Backend push, email subject, and Notification Center go out on the next send. Email body needs a pull request, or Live in flow on a Klaviyo lane. Live in flow: the words live in Klaviyo and are recorded here.',
  notificationCenter: 'How the notification appears in the creator\u2019s in-app Notification Center and which image leads the row.',
  draftWithAi:
    'Writes a first version from Purpose, Trigger and Inputs, within each limit and using only the inputs as variables. It lands as a draft with Draft with AI named in History; a person still approves it.'
};

export const approveWarningFor = ({ surface, sentFrom, hasTemplate = false, pushDelivery = null, emailDelivery = null }) => {
  if (approvalReplacesLiveSend({ surface, sentFrom, hasTemplate, pushDelivery, emailDelivery }))
    return 'Confirming will change the live copy users see the next time this notification is sent.';
  if ((!hasTemplate && sentFrom === 'klaviyo_flow') || isKlaviyoDeliveredSurface({ surface, sentFrom, pushDelivery, emailDelivery }))
    return 'Confirming records the approved words here. They do not go live until Lifecycle marks this surface Live in flow.';
  if (!hasTemplate && sentFrom === 'onesignal_dashboard')
    return 'Confirming records the approved words here. They do not change what users receive until they are live in that system.';
  return 'Confirming records the approved words here. They do not change what users see until a pull request ships the template.';
};

export const withdrawWarningFor = ({ surface, sentFrom, hasTemplate = false, pushDelivery = null, emailDelivery = null }) => {
  const label = SURFACE_LABELS[surface] || surface;
  if (approvalReplacesLiveSend({ surface, sentFrom, hasTemplate, pushDelivery, emailDelivery }))
    return `The next send goes back to the template's words for ${label}. The approved versions stay in History.`;
  return `Withdrawing records that ${label} has no approved words. It does not change a live send. The approved versions stay in History.`;
};

export const revokeWarningFor = ({ surface, sentFrom, hasTemplate = false, pushDelivery = null, emailDelivery = null, hasDraft = false }) => {
  const label = SURFACE_LABELS[surface] || surface;
  const draftMessage = hasDraft
    ? 'Your current draft stays editable, and the approved version stays in History.'
    : 'The approved words stay in the field as an editable draft.';
  if (approvalReplacesLiveSend({ surface, sentFrom, hasTemplate, pushDelivery, emailDelivery }))
    return `Revoking removes the active Registry words for ${label}. The next send goes back to the template's words. ${draftMessage} The launch checklist may stop passing.`;
  return `Revoking removes Registry sign-off for ${label}. It does not change copy outside the Registry. ${draftMessage} The launch checklist may stop passing.`;
};

export const UNSET_MODE_NOTE = 'This is in Shadow because no one has chosen otherwise. Nothing is sent until someone moves it to Allowlist or Live.';

// The moves a backend notification can make from where it sits. Paused resumes through Live only; Retired has none.
// Leaving Shadow needs the gate; `locked` keeps the move visible with the reason it cannot run yet.
export const launchMovesFor = ({ stage, mode, gatePassed, lockedReason = null }) => {
  if (stage === 'retired') return [];
  if (stage === 'paused') return [{ mode: 'live', label: 'Resume Live', quiet: true }];
  if (stage === 'live') {
    return [
      { mode: 'shadow', label: 'Pause' },
      { mode: 'allowlist', label: 'Narrow to Allowlist' }
    ];
  }
  if (mode === 'allowlist') {
    return [
      { mode: 'live', label: 'Move to Live' },
      { mode: 'shadow', label: 'Move to Shadow' }
    ];
  }
  const lock = gatePassed ? {} : { locked: true, lockedReason: lockedReason || 'Locked until the gate passes' };
  return [
    { mode: 'allowlist', label: 'Move to Allowlist', ...lock },
    { mode: 'live', label: 'Move to Live', ...lock }
  ];
};

// What the Mode buttons are about to do. Both the Registry entry and Launches ask with this before they save.
export const modeChangeConfirm = ({ name, mode, fromMode, status, holdoutPercent, allowlistCount = 0 }) => {
  const holdout = holdoutPercent || 0;
  const people = pluralizeCount(allowlistCount, 'person', 'people');
  const move = modeMoveFor({ mode, fromMode, status });
  if (move === 'resume')
    return {
      title: `Resume ${name}?`,
      message: `It starts sending again from its next send${
        holdout ? `, except the ${holdout}% held out` : ''
      }. Anything that fired while it was paused isn't sent.`,
      confirm: 'Resume Live'
    };
  if (move === 'live')
    return {
      title: `Move ${name} to Live?`,
      message: holdout
        ? `It starts sending from its next send, except ${holdout}% of people, who are held back from the push and the email to compare against. They still get the Notification Center row. It stays on Launches until the holdout is set to No.`
        : `It starts sending to everyone it's for${
            fromMode === 'allowlist' ? `, not just the ${people} on its list,` : ''
          } from its next send. It then leaves Launches.`,
      confirm: 'Move to Live'
    };
  if (move === 'narrow')
    return {
      title: `Narrow ${name} to Allowlist?`,
      message: allowlistCount
        ? `Everyone except the ${people} on its list stops getting it.`
        : 'Everyone stops getting it until you add people to its list.',
      confirm: 'Narrow to Allowlist'
    };
  if (move === 'allowlist')
    return {
      title: `Move ${name} to Allowlist?`,
      message: allowlistCount
        ? `Only the ${people} on its list get it. Everyone else stays in Shadow.`
        : 'Only the people on its list get it. Everyone else stays in Shadow. Its list is empty, so add people once it moves.',
      confirm: 'Move to Allowlist'
    };
  if (move === 'pause')
    return {
      title: `Pause ${name}?`,
      message: "It stops sending to everyone right away. Pushes already sent can't be recalled. Resume Live starts it again.",
      confirm: 'Pause'
    };
  return {
    title: `Move ${name} back to Shadow?`,
    message: allowlistCount
      ? `The ${people} on its list stop getting it, and it goes back to recording what it would send. The list is kept.`
      : 'It goes back to recording what it would send. Nothing sends.',
    confirm: 'Move to Shadow'
  };
};

// The toast once a Mode move saves, read from the same state the confirm was.
export const modeChangeToast = ({ name, mode, fromMode, status, holdoutPercent, allowlistCount = 0 }) => {
  const holdout = holdoutPercent || 0;
  const people = pluralizeCount(allowlistCount, 'person', 'people');
  const move = modeMoveFor({ mode, fromMode, status });
  if (move === 'resume') return holdout ? `${name} is live again, with ${holdout}% held out.` : `${name} is live again. ${SENDS_TAB_POINTER}`;
  if (move === 'live')
    return holdout
      ? `${name} is now live, with ${holdout}% held out. It stays on Launches until the holdout is set to No.`
      : `${name} is now live. ${SENDS_TAB_POINTER}`;
  if (move === 'narrow')
    return allowlistCount ? `${name} now sends only to the ${people} on its list.` : `${name} is on Allowlist with nobody on it, so nothing sends.`;
  if (move === 'allowlist')
    return allowlistCount ? `${name} is on Allowlist, sending to ${people}.` : `${name} is on Allowlist. Add people to start sending.`;
  if (move === 'pause') return `${name} is paused. Nothing sends until you resume it.`;
  return `${name} is back in Shadow. Nothing sends.`;
};

// Changing the holdout of a Live notification changes who gets it on the next send, so it asks first.
// Before Live the holdout is only a plan, and so is a return to the default, whose percent the server decides.
export const holdoutChangeConfirm = ({ name, live, from, to }) => {
  if (!live || to == null || to === from) return null;
  if (!to)
    return {
      title: `Send ${name} to everyone?`,
      message: `The ${from}% held out start getting the push and the email from the next send. It then leaves Launches.`,
      confirm: 'Set to No'
    };
  if (!from)
    return {
      title: `Hold out ${to}% from ${name}?`,
      message: `${to}% of people stop getting the push and the email from the next send, so you can compare against them. They still get the Notification Center row.`,
      confirm: `Set to ${to}%`
    };
  return to > from
    ? {
        title: `Raise the ${name} holdout to ${to}%?`,
        message: `${to}% of people stop getting the push and the email, up from ${from}%. They still get the Notification Center row.`,
        confirm: `Set to ${to}%`
      }
    : {
        title: `Lower the ${name} holdout to ${to}%?`,
        message: `${to}% are held out, down from ${from}%. The rest start getting the push and the email from the next send.`,
        confirm: `Set to ${to}%`
      };
};

export const holdoutChangeToast = ({ name, live, percent }) => {
  if (!percent) return live ? `${name} now sends to everyone. ${SENDS_TAB_POINTER}` : 'Holdout set to No.';
  return live ? `Holdout set to ${percent}%.` : `Holdout set to ${percent}%. It applies once ${name} is Live.`;
};

// What Retire means from the state it is in now. A later state is a different sentence.
export const retireConfirmFor = ({
  name,
  stage,
  allowlistCount = 0,
  sentFrom = null,
  hasTemplate = false,
  holdoutPercent = 0,
  ownsSharedWords = false
}) => {
  const title = `Retire ${name}?`;
  const people = pluralizeCount(allowlistCount, 'person', 'people');
  const say = message => ({
    title,
    message: ownsSharedWords ? `${message} Its shared row keeps using these words until someone moves them.` : message
  });
  if (!sendsFromBackend({ sentFrom, hasTemplate })) {
    const where = sentFrom === 'onesignal_dashboard' ? 'OneSignal' : 'Klaviyo';
    return say(`${where} sends this. Retiring takes it out of the Registry list. Those sends keep going until someone stops them there.`);
  }
  if (stage === 'allowlist')
    return say(
      allowlistCount
        ? `It's sending to the ${people} on its list. Retiring stops those sends right away and takes it off Launches.`
        : "It's on Allowlist with nobody on the list, so nothing is sending. Retiring takes it off Launches."
    );
  if (stage === 'live')
    return say(
      holdoutPercent
        ? "It's Live. Retiring stops the sends right away and takes it off Launches."
        : "It's Live. Retiring stops the sends right away."
    );
  if (stage === 'paused')
    return say("It's paused, so nothing is sending. Retiring takes it off Launches, and it can no longer be resumed.");
  if (stage === 'shadow')
    return say("It's in Shadow, so nothing is sending. Retiring takes it off Launches, and it can no longer move to Allowlist or Live.");
  return say("It's Planned, so nothing is sending. Retiring takes it out of the Registry list. You can restore it to Planned later.");
};

export const retireToast = ({ name, sentFrom = null, hasTemplate = false }) => {
  if (!sendsFromBackend({ sentFrom, hasTemplate })) {
    const where = sentFrom === 'onesignal_dashboard' ? 'OneSignal' : 'Klaviyo';
    return `${name} is retired. ${where} keeps sending until someone stops it there.`;
  }
  return `${name} is retired. Nothing sends.`;
};

export const restoreToast = name => `${name} is Planned again. It does not send until someone moves it.`;

const SENDS_TAB_POINTER = 'See its performance in the Sends tab.';

const modeMoveFor = ({ mode, fromMode, status }) => {
  const wasLive = fromMode === 'live' || status === 'live';
  if (mode === 'live') return status === 'paused' ? 'resume' : 'live';
  if (mode === 'allowlist') return wasLive ? 'narrow' : 'allowlist';
  return wasLive ? 'pause' : 'shadow';
};

export const humanizeStatus = status => STATUS_LABELS[status] || status || '—';
export const humanizeStage = stage => STAGE_LABELS[stage] || stage || '—';
export const humanizeSentFrom = (sentFrom, { pushDelivery = null } = {}) =>
  backendDeliveredByKlaviyo({ sentFrom, pushDelivery }) ? DELIVERED_BY_KLAVIYO_LABEL : sentFrom ? SENT_FROM_LABELS[sentFrom] || sentFrom : 'Not decided';
export const humanizeChannels = channels =>
  CHANNEL_ORDER.filter(channel => (channels || []).includes(channel))
    .map(channel => CHANNEL_LABELS[channel])
    .join(', ');
export const humanizeCenter = rulebook =>
  !rulebook?.centerBehavior || rulebook.centerBehavior === 'none'
    ? 'No row'
    : `${isEntityThread(rulebook) ? 'Live Thread' : CENTER_BEHAVIOR_LABELS[rulebook.centerBehavior] || rulebook.centerBehavior}${
        rulebook.centerImageOf ? ` · ${CENTER_IMAGE_SHORT[rulebook.centerImageOf]}` : ''
      }`;
const CENTER_IMAGE_SHORT = { link: 'product', brand: 'brand', general: 'ShopMy' };

export const humanizeBudgetCategory = humanizeCategory;

// The list's Gate cell: Ready, Blocked with the failing checks in short form, or the honest state for a
// notification that shipped before the gate existed.
export const gateSummary = row => {
  if (row.status === 'paused' || row.status === 'retired') return { kind: 'none', label: '—' };
  if (row.gate.passed) return { kind: row.status === 'live' ? 'already_live' : 'ready', label: row.status === 'live' ? 'Already live' : 'Ready' };
  if (row.status === 'live') return { kind: 'ungated', label: 'Ungated' };
  return { kind: 'blocked', label: `Blocked · ${row.gate.failingTasks.map(_.toLower).join(', ')}` };
};

// The switches an audience has: brands have their own Settings page.
export const settingsGroupsFor = (options, audiences = []) => {
  if (!options) return [];
  const permittedIds = new Set(notificationSettings.settingGroupsForAudiences(audiences).map(group => group.id));
  return [...(options.settingsGroups || []), ...(options.brandSettingsGroups || [])].filter(group => permittedIds.has(group.id));
};

// A shipped switch the plan declares that the audience's groups no longer offer, so the select can
// keep it rather than save over it. A proposed switch is not shipped, so it never counts.
export const declaredSwitchOutsideGroups = (options, entry, groups) => {
  const setting = entry.declaredUserNotificationSetting;
  if (!setting || setting === 'none' || entry.newSwitchDefinition?.setting === setting) return null;
  if (groups.some(group => group.options.some(option => option.setting === setting))) return null;
  const shipped = [...(options?.settingsGroups || []), ...(options?.brandSettingsGroups || [])].flatMap(group => group.options);
  return shipped.find(option => option.setting === setting) || { setting, label: setting };
};

// "amount: 907" per line, into the Inputs list; a bare name is allowed while the example is unknown.
const NAMED_INPUT_PAIR = /^\s*[A-Za-z_][\w.]*\s*:/;
export const parseInputsText = text =>
  String(text || '')
    .split('\n')
    // Several "name: example" pairs on one line, split on /, comma or semicolon. A separator
    // inside a single example (a URL, 1,000, a date) stays, because the pieces would not each be named.
    .flatMap(line => {
      const parts = line.split(/\s*[/,;]\s*/).filter(part => part.trim());
      const everyPartIsNamed = parts.length > 1 && parts.every(part => NAMED_INPUT_PAIR.test(part));
      return everyPartIsNamed ? parts : [line];
    })
    .map(line => line.trim())
    .filter(Boolean)
    .map(line => {
      const [name, ...rest] = line.split(':');
      return { name: name.trim(), example: rest.join(':').trim() };
    })
    .filter(input => /^[A-Za-z_][\w.]*$/.test(input.name));

export const formatInputsText = inputs => (inputs || []).map(input => (input.example ? `${input.name}: ${input.example}` : input.name)).join('\n');
